import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { AggregatedMetadata } from '../../types/algorhythm.types';

interface AggregatedMetadataDisplayProps {
  metadata: AggregatedMetadata;
  title?: string;
  subtitle?: string;
}

/**
 * Read-only display component for Aggregated metadata (synergy scores)
 * Used in Composite asset edit pages to show component compatibility
 */
export const AggregatedMetadataDisplay: React.FC<AggregatedMetadataDisplayProps> = ({
  metadata,
  title = '✨ Aggregated Metadata',
  subtitle = 'Component Compatibility Analysis',
}) => {
  if (!metadata) {
    return null;
  }

  // Helper to convert 0-1 score to percentage
  const toPercentage = (score: number | undefined): number => {
    if (score === undefined || score === null) return 0;
    return Math.round(score * 100);
  };

  // Helper to get color based on score
  const getScoreColor = (score: number): 'error' | 'warning' | 'success' => {
    if (score < 40) return 'error';
    if (score < 70) return 'warning';
    return 'success';
  };

  const synergyPercentage = toPercentage(metadata.synergyScore);
  const synergyColor = getScoreColor(synergyPercentage);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>

      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {subtitle}
        </Typography>
      )}

      {/* Synergy Score (Main Score) */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Overall Synergy Score
          </Typography>
          <Chip
            label={`${synergyPercentage}%`}
            color={synergyColor}
            size="medium"
            sx={{ fontWeight: 'bold', fontSize: '1rem' }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={synergyPercentage}
          color={synergyColor}
          sx={{ height: 10, borderRadius: 5 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Higher scores indicate better component compatibility
        </Typography>
      </Box>

      {/* Synergy Breakdown Factors */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
        Synergy Breakdown
      </Typography>

      <Grid container spacing={2}>
        {/* Visual Cohesion */}
        <Grid item xs={12} md={6}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Visual Cohesion
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {toPercentage(metadata.visualCohesion)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={toPercentage(metadata.visualCohesion)}
              color={getScoreColor(toPercentage(metadata.visualCohesion))}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Grid>

        {/* Cultural Alignment */}
        <Grid item xs={12} md={6}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Cultural Alignment
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {toPercentage(metadata.culturalAlignment)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={toPercentage(metadata.culturalAlignment)}
              color={getScoreColor(toPercentage(metadata.culturalAlignment))}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Grid>

        {/* Energy Balance */}
        <Grid item xs={12} md={6}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Energy Balance
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {toPercentage(metadata.energyBalance)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={toPercentage(metadata.energyBalance)}
              color={getScoreColor(toPercentage(metadata.energyBalance))}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Grid>

        {/* Audience Match */}
        <Grid item xs={12} md={6}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Audience Match
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {toPercentage(metadata.audienceMatch)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={toPercentage(metadata.audienceMatch)}
              color={getScoreColor(toPercentage(metadata.audienceMatch))}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Grid>

        {/* Thematic Coherence */}
        <Grid item xs={12} md={6}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Thematic Coherence
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {toPercentage(metadata.thematicCoherence)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={toPercentage(metadata.thematicCoherence)}
              color={getScoreColor(toPercentage(metadata.thematicCoherence))}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Dominant Colors & Mood */}
      {(metadata.dominantColors || metadata.dominantMood) && (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
          <Grid container spacing={2}>
            {metadata.dominantColors && metadata.dominantColors.length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Dominant Colors
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {metadata.dominantColors.map((color, index) => (
                    <Chip
                      key={index}
                      label={color}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {metadata.dominantMood && metadata.dominantMood.length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Dominant Mood
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {metadata.dominantMood.map((mood, index) => (
                    <Chip
                      key={index}
                      label={mood}
                      size="small"
                      variant="outlined"
                      color="success"
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default AggregatedMetadataDisplay;
